
import Vue from 'vue';
import { mapState, mapGetters, mapActions } from 'vuex';

import FormInput from '@/components/controls/form-input.vue';
import SubmitButton from '@/components/controls/submit-button.vue';
import NotificationBanner from '@/components/blocks/notification-banner.vue';
import Publish from '@/components/forms/publish.vue';
import ImageUploadControl from '@/components/controls/image-upload.vue';
import ProductSelectionControl from '@/components/controls/product-selection.vue';
import CountrySelection from '@/components/controls/country-selection.vue';
import MultiSelectInput from '@/components/controls/multiselect-input.vue';
import Autocomplete from '@/components/controls/autocomplete.vue';
import SvgIcon from '@/components/controls/svg-icon.vue';

import { AdStatus } from '@/store/modules/ads/ad';
import { AdsActions } from '@/store/modules/ads/types';

export default Vue.extend({
  name: 'EditAdView',
  inject: {
    $validator: '$validator',
  },
  components: {
    FormInput,
    SubmitButton,
    NotificationBanner,
    Publish,
    ImageUploadControl,
    ProductSelectionControl,
    CountrySelection,
    MultiSelectInput,
    Autocomplete,
  },
  props: {
    add: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      productsInput: '',
      countriesInput: '',
    };
  },
  computed: {
    ...mapState('ads', {
      ad: 'editable',
      editableStatus: 'editableStatus',
      products: 'products',
      countries: 'countries',
    }),
    ...mapGetters('ads', {
      isLoading: 'editableIsLoading',
      isSaving: 'editableIsSaving',
      hasUnsavedChanges: 'editableHasUnsavedChanges',
    }),
    isFetching() {
      return this.isLoading || this.isSaving;
    },
    isInactive() {
      return this.ad?.status === AdStatus.Inactive;
    },
    getProducts() {
      const selectedIds = this.ad.products.map(p => p.id);
      return this.products
        .filter(product => !selectedIds.includes(product.id))
        .filter(product => product.title.toLowerCase().includes(this.productsInput.toLowerCase()))
        .map(product => ({
          label: product.title,
          value: product.id,
        }));
    },
    getCountries() {
      const selectedIds = this.ad.countries.map(c => c.id);
      return this.countries
        .filter(country => !selectedIds.includes(country.id))
        .filter(country => country.name.toLowerCase().includes(this.countriesInput.toLowerCase()))
        .map(country => ({
          label: country.name,
          value: country.id,
        }));
    },
  },
  methods: {
    ...mapActions('ads', {
      updateEditable: AdsActions.UPDATE_EDITABLE,
      save: AdsActions.SAVE,
      removeAd: AdsActions.DELETE,
      restoreAd: AdsActions.RESTORE,
      loadAd: AdsActions.LOAD_EDITABLE,
      loadProducts: AdsActions.LOAD_PRODUCTS,
      loadCountries: AdsActions.LOAD_COUNTRIES,
    }),
    remove() {
      this.removeAd({ id: this.ad.id });
    },
    restore() {
      this.restoreAd({ id: this.ad.id });
    },
    handleSave() {
      this.save();
    },
    removeProduct(id) {
      this.updateEditable({ products: this.ad.products.filter(p => p.id !== id) });
    },
    addProduct(option) {
      this.updateEditable({ products: [...this.ad.products, { id: option.value, title: option.label }] });
    },
    removeCountry(id) {
      this.updateEditable({ countries: this.ad.countries.filter(c => c.id !== id) });
    },
    addCountry(option) {
      this.updateEditable({ countries: [...this.ad.countries, { id: option.value, name: option.label }] });
    },
  },
  created() {
    this.loadAd({ id: this.add ? undefined : this.$route.params.id });
    this.loadProducts();
    this.loadCountries();
  },
  beforeRouteLeave(to, from, next) {
    if (this.hasUnsavedChanges) {
      const answer = window.confirm(this.$t('Do you really want to leave? You have unsaved changes!'));
      if (!answer) {
        next(false);
        return;
      }
    }
    next();
  },
});
